import React, {
  useCallback, useContext, useEffect, useRef, useState, useMemo,
} from 'react';
import styled from 'styled-components';
import type { TransitionStatus } from 'react-transition-group';
import { CSSTransition } from 'react-transition-group';
import ReactDOM from 'react-dom';

import t from '@zimpel/common/helpers/i18n';
import { PrimaryButton, SecondaryButton } from '../Buttons';
import { BigBlock } from '../Blocks';
import { CloseIcon, InfoIcon, WarningIcon } from '../Icon';
import ButtonGroup from '../ButtonGroup/ButtonGroup';

type AnimatedProps = {
  $inState: boolean;
}

const StyledFixedBg = styled.div`
  position: fixed;
  inset: 0;
`;

const StyledModal = styled(StyledFixedBg) <AnimatedProps>`
  transition: opacity 200ms ease-out;
  z-index: 99;
  opacity: ${(p) => (p.$inState ? '1' : '0')};
`;

const StyledClickLayer = styled(StyledFixedBg) <AnimatedProps>`
  transition: opacity 400ms ease-out;
  background-color: #000;
  opacity: ${(p) => (p.$inState ? '0.4' : '0')};
`;

const StyledModalContent = styled.div`
  position: relative;
  max-width: 600px;
  margin: 9vh auto 0;
`;

const StyledBox = styled.div`
  width: 100%;
  border-radius: var(--border-box-radius);
  padding: 20px;
  background-color: var(--color-white);
  box-shadow: 0 1px 20px 0 rgba(0 0 0 / 20%);
`;

const StyledDialogueTitle = styled.h1`
  padding-bottom: 3px;
  margin-top: 11px;
  font-size: var(--font-size-large);
`;

const StyledBoxTitle = styled.h1`
  margin: 0 0 12px;
  font-size: var(--font-size-large);
`;

const StyledWarningIcon = styled(WarningIcon)`
  color: var(--color-warning);
`;

const StyledCenteredText = styled.div`
  text-align: center;
`;

const StyledCloseButton = styled(PrimaryButton)`
  float: right;
  
  svg {
    position: relative;
    top: -1px;
  }
`;

export const StyledModalScrollBody = styled.div`
  max-height: 75vh; 
  overflow-y: scroll;
`;

export interface ModalProps extends React.PropsWithChildren {
  show: boolean;
  onAskedForClose?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  children, show, onAskedForClose, ...rest
}) => {
  const { element } = useContext(ModalContext);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const escListner = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show && onAskedForClose) {
        onAskedForClose();
      }
    };

    if (onAskedForClose) {
      document.addEventListener('keydown', escListner);
    }

    return () => document.removeEventListener('keydown', escListner);
  }, [show, onAskedForClose]);

  if (!element) {
    return null;
  }

  const inStateFromTransitionState = (status: TransitionStatus): boolean => status === 'entered' || status === 'entering';

  return ReactDOM.createPortal(
    <CSSTransition
      in={show}
      appear
      mountOnEnter
      unmountOnExit
      timeout={process.env.NODE_ENV === 'test' ? 0 : 500}
      nodeRef={nodeRef}
    >
      {(state) => (
        <StyledModal ref={nodeRef} {...rest} $inState={inStateFromTransitionState(state)}>
          <StyledClickLayer
            $inState={inStateFromTransitionState(state)}
            onClick={() => onAskedForClose && onAskedForClose()}
            data-test="modal-click-layer"
          />

          <StyledModalContent>
            <StyledBox>{children}</StyledBox>
          </StyledModalContent>
        </StyledModal>
      )}
    </CSSTransition>,
    element,
  );
};

type VoidFn = () => void;

type ModalTitleProps = {
  onAskedForClosed?: VoidFn
} & React.PropsWithChildren;
export const ModalTitle: React.FC<ModalTitleProps> = ({ children, onAskedForClosed }) => (
  <>
    {onAskedForClosed && (
    <StyledCloseButton onClick={onAskedForClosed} data-test="modal-close-btn">
      <CloseIcon rightMargin={false} />
    </StyledCloseButton>
    )}
    <StyledBoxTitle>{children}</StyledBoxTitle>
  </>
);

type ConfirmDialogueProps = {
  show: boolean;
  onAskedForClosed: VoidFn;
  okCb: VoidFn;
  cancelCb: VoidFn;
} & React.PropsWithChildren;

const ConfirmDialogue: React.FC<ConfirmDialogueProps> = ({
  show, onAskedForClosed, okCb, cancelCb, children,
}) => (
  <Modal show={show} onAskedForClose={onAskedForClosed}>
    <StyledCenteredText>
      <InfoIcon size="2x" rightMargin={false} />
      <StyledDialogueTitle>{t('dialogue.confirmation.title')}</StyledDialogueTitle>
      {children}
      <BigBlock>
        <ButtonGroup align="CENTER">
          <PrimaryButton $width="m" onClick={okCb} data-test="confirm-dialogue-ok">
            {t('dialogue.confirmation.ok')}
          </PrimaryButton>
          <SecondaryButton $width="m" onClick={cancelCb} data-test="confirm-dialogue-cancel">
            {t('dialogue.confirmation.cancel')}
          </SecondaryButton>
        </ButtonGroup>
      </BigBlock>
    </StyledCenteredText>
  </Modal>
);

type AlertDialogueProps = {
  show: boolean;
  onAskedForClosed: VoidFn;
  okCb: VoidFn;
} & React.PropsWithChildren;

const AlertDialogue: React.FC<AlertDialogueProps> = ({
  show, onAskedForClosed, okCb, children,
}) => (
  <Modal show={show} onAskedForClose={onAskedForClosed}>
    <StyledCenteredText>
      <StyledWarningIcon size="2x" rightMargin={false} />
      <StyledDialogueTitle>{t('dialogue.alert.title')}</StyledDialogueTitle>
      {children}
      <BigBlock>
        <PrimaryButton $width="m" onClick={okCb} data-test="alert-dialogue-ok">
          {t('dialogue.confirmation.ok')}
        </PrimaryButton>
      </BigBlock>
    </StyledCenteredText>
  </Modal>
);

export type ModalContextType = {
  element?: HTMLDivElement | null;
  confirm: (msg: React.ReactNode, resolvePromiseOnCancel?:boolean) => Promise<boolean>;
  alert: (msg: React.ReactNode) => Promise<boolean>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const defaultConfirm = (msg: React.ReactNode) => new Promise<boolean>(() => {
  throw new Error('Not initialized!');
});

export const ModalContext: React.Context<ModalContextType> = React.createContext({
  confirm: defaultConfirm,
  alert: defaultConfirm,
});

export const ModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [confirmContent, setConfirmContent] = useState<React.ReactNode | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const [alertContent, setAlertContent] = useState<React.ReactNode | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const confirmRes = useRef<((result: boolean) => void) | null>(null);

  const [resolvePromiseOnCancel, setResolvePromiseOnCancel] = useState<boolean>(false);

  const confirm = (msg: React.ReactNode, resolvePromiseOnCancel:boolean = false) => new Promise<boolean>((res) => {
    confirmRes.current = res;
    setConfirmContent(msg);
    setShowConfirm(true);
    setResolvePromiseOnCancel(resolvePromiseOnCancel);
  });

  const alert = (msg: React.ReactNode) => new Promise<boolean>((res) => {
    confirmRes.current = res;
    setAlertContent(msg);
    setShowAlert(true);
  });

  const modalContainerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setElement(node);
    }
  }, []);

  const contextValue = useMemo(() => ({
    element,
    confirm,
    alert,
  }), [element]);

  return (
    <ModalContext.Provider value={contextValue}>
      {element && children}

      <ConfirmDialogue
        show={showConfirm}
        onAskedForClosed={() => setShowConfirm(false)}
        okCb={() => {
          if (confirmRes.current) {
            confirmRes.current(true);
          }
          setShowConfirm(false);
        }}
        cancelCb={() => {
          if (confirmRes.current && resolvePromiseOnCancel) {
            confirmRes.current(false);
          }
          setShowConfirm(false);
        }}
      >
        {confirmContent}
      </ConfirmDialogue>

      <AlertDialogue
        show={showAlert}
        onAskedForClosed={() => setShowAlert(false)}
        okCb={() => {
          if (confirmRes.current) {
            confirmRes.current(true);
          }
          setShowAlert(false);
        }}
      >
        {alertContent}
      </AlertDialogue>

      <div ref={modalContainerRef} />
    </ModalContext.Provider>
  );
};
